import React, { FC, useState } from 'react'
import { Button, Skeleton } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { IDefaultPricingCardProps } from '../interfaces'
import { InlineDefaultPricingEdit } from './InlineDefaultPricingEdit'
import { formatCurrency } from '../utils/formatters'

export const DefaultPricingCard: FC<IDefaultPricingCardProps> = ({
  defaultCustomer,
  onEdit,
  loading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleSaveInlineEdit = async (values: {
    largeIceCubePriceTHB: number
    smallIceCubePriceTHB: number
    crushedIcePriceTHB: number
  }) => {
    if (defaultCustomer) {
      // Convert to the format expected by onEdit
      const formData = {
        name: defaultCustomer.name,
        largeIceCubePriceTHB: values.largeIceCubePriceTHB,
        smallIceCubePriceTHB: values.smallIceCubePriceTHB,
        crushedIcePriceTHB: values.crushedIcePriceTHB,
      }
      await onEdit(defaultCustomer, formData)
      setIsEditing(false)
    }
  }

  const handleCancelInlineEdit = () => {
    setIsEditing(false)
  }

  const formatPrice = (price: number | undefined) => {
    return formatCurrency(price)
  }

  if (loading) {
    return (
      <div className='bg-white border rounded-lg p-6 mb-6'>
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    )
  }

  // Show inline editing form if editing
  if (isEditing && defaultCustomer) {
    const initialValues = {
      largeIceCubePriceTHB:
        defaultCustomer.pricing?.large_ice_cube_price_thb || 0,
      smallIceCubePriceTHB:
        defaultCustomer.pricing?.small_ice_cube_price_thb || 0,
      crushedIcePriceTHB: defaultCustomer.pricing?.crushed_ice_price_thb || 0,
    }

    return (
      <div className='mb-6'>
        <InlineDefaultPricingEdit
          initialValues={initialValues}
          onSave={handleSaveInlineEdit}
          onCancel={handleCancelInlineEdit}
          loading={loading}
        />
      </div>
    )
  }

  return (
    <div className='bg-white border rounded-lg p-6 mb-6 shadow-sm'>
      {/* Price Display with Grid Layout - Title in same row, left aligned */}
      <div className='grid grid-cols-12 gap-4 items-center'>
        {/* Title - Left aligned, spans from start */}
        <div className='col-span-4'>
          <h2 className='text-lg font-semibold text-gray-800'>
            Default Customer Pricing
          </h2>
        </div>

        {/* Large Ice Price */}
        <div className='col-span-2 text-center'>
          <div className='text-sm text-gray-600 mb-1'>ใหญ่</div>
          <div className='text-lg font-medium text-gray-800 font-mono'>
            {formatPrice(defaultCustomer?.pricing?.large_ice_cube_price_thb)}
          </div>
        </div>

        {/* Small Ice Price */}
        <div className='col-span-2 text-center'>
          <div className='text-sm text-gray-600 mb-1'>เล็ก</div>
          <div className='text-lg font-medium text-gray-800 font-mono'>
            {formatPrice(defaultCustomer?.pricing?.small_ice_cube_price_thb)}
          </div>
        </div>

        {/* Crushed Ice Price */}
        <div className='col-span-2 text-center'>
          <div className='text-sm text-gray-600 mb-1'>โม่</div>
          <div className='text-lg font-medium text-gray-800 font-mono'>
            {formatPrice(defaultCustomer?.pricing?.crushed_ice_price_thb)}
          </div>
        </div>

        {/* Edit Button */}
        <div className='col-span-2 flex justify-end'>
          <Button
            type='default'
            icon={<EditOutlined />}
            onClick={handleEdit}
            disabled={!defaultCustomer || loading}
            className='flex items-center'
          >
            Edit
          </Button>
        </div>
      </div>

      {!defaultCustomer && (
        <div className='text-center text-gray-500 mt-4'>
          Default pricing data not found
        </div>
      )}
    </div>
  )
}
